// @wc-ignore-file
// Pure AI-routing logic for chat rooms (CR-25). Kept free of React/supabase so
// it can be unit-tested in isolation. The "kairo" literal below is data, not a
// user-facing string — @wc-ignore-file stops wuchale from extracting/translating
// it (which would break the comparison under a non-English locale).
import type { AiMember } from "@shared/types/supabase.types";

/**
 * Whether `text` @mentions `handle` as a whole token rather than a bare
 * substring — so "@ai" does not match "@aide" and an email like "x@ai.com"
 * doesn't count. Must stay in sync with the backend copy in
 * backend/api-server/src/routes/ai-llm-orchestrator/room-policy.ts (runtime code is
 * not shared across the frontend/backend boundary in this repo — see CLAUDE.md);
 * the client and server must agree or a mention accepted by one is refused by
 * the other.
 */
export function isHandleMentioned(text: string, handle: string): boolean {
  if (!handle) return false;
  const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|\\s)${escaped}(?=\\s|$|[^\\w])`).test(text);
}

/**
 * CR-220: whether `text` @mentions one of the human teammates `displayNames` as a
 * whole token (the picker inserts `@First Last`, so names hold spaces),
 * case-insensitive, ignoring `>` quoted lines and code. Mirrors `stripQuotedAndCode`
 * + `findMentionedHumans` in
 * backend/api-server/src/routes/ai-llm-orchestrator/human-address.ts — keep in sync:
 * the reply chip steps aside on exactly the messages the server's gate examines.
 */
export function mentionsHuman(text: string, displayNames: Iterable<string>): boolean {
  const body = text
    .replace(/```[\s\S]*?(```|$)/g, " ")
    .replace(/`[^`\n]*`/g, " ")
    .split("\n")
    .filter((line) => !/^\s*>/.test(line))
    .join("\n");
  if (!/(^|\s)@\S/.test(body)) return false;
  for (const displayName of displayNames) {
    const name = displayName.trim();
    if (!name) continue;
    // Syntax characters only: under the `u` flag an escaped `-` or other
    // non-syntax character is a SyntaxError.
    const escaped = `@${name}`.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (new RegExp(`(^|\\s)${escaped}(?=\\s|$|[^\\p{L}\\p{N}_])`, "iu").test(body)) {
      return true;
    }
  }
  return false;
}

/**
 * The AI personas a message can @mention in a room (CR-25 follow-up).
 *
 * A persona participates — and is therefore @mentionable — when the room shares
 * all project personas (isAiShared) or the persona is an explicit room member.
 * The same rule applies to AI Chats and Message rooms: membership (explicit or
 * shared) is the single source of truth for who can be addressed.
 */
export function getMentionCandidates(params: {
  isAiShared: boolean;
  projectAiPersonas: AiMember[];
  roomAiPersonas: AiMember[];
}): AiMember[] {
  const { isAiShared, projectAiPersonas, roomAiPersonas } = params;
  return isAiShared ? projectAiPersonas : roomAiPersonas;
}

/**
 * The AI Chat's "active persona" — the one that auto-replies when no persona is
 * @mentioned.
 *
 * `defaultAiPersonaId` is the room's explicit selection
 * (`containers.default_ai_persona_id`, CR-46) and wins whenever it still
 * resolves against `aiPersonas`. It is re-resolved on every call rather than
 * trusted: the column is not cleared when a persona merely leaves the room, and
 * an `is_ai_shared` room can even carry an id the DB never validated for
 * participation (the RPC checks the FK only — see the Phase A migration). A
 * stale or foreign id is therefore inert and falls through to the original
 * rule: Kairo when present, otherwise the first AI member.
 *
 * Callers must pass the room's *participating* personas — all project personas
 * for an `is_ai_shared` room, the explicit AI members otherwise — so a default
 * set on an implicit persona routes as selected.
 */
export function getActivePersona(
  aiPersonas: AiMember[],
  defaultAiPersonaId?: string | null,
): AiMember | undefined {
  if (aiPersonas.length === 0) return undefined;
  if (defaultAiPersonaId) {
    const selected = aiPersonas.find((persona) => persona.ai_persona_id === defaultAiPersonaId);
    if (selected) return selected;
  }
  const kairo = aiPersonas.find(
    (persona) => persona.ai_persona_display_name?.toLowerCase() === "kairo",
  );
  return kairo ?? aiPersonas[0];
}

/**
 * Decide which AI personas respond to a human message.
 *
 * - AI Chat (isAiChat): AI-first — mentioned personas reply; if none are
 *   mentioned, the active persona replies.
 * - Message room: human-first — only explicitly @mentioned personas reply; an
 *   un-mentioned message gets no AI reply.
 */
export function selectRespondingPersonas(params: {
  isAiChat: boolean;
  mentionedAiPersonas: AiMember[];
  activePersona: AiMember | undefined;
}): AiMember[] {
  const { isAiChat, mentionedAiPersonas, activePersona } = params;
  if (mentionedAiPersonas.length > 0) return mentionedAiPersonas;
  if (isAiChat && activePersona) return [activePersona];
  return [];
}
